// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { FloatingTooltipStyles } from "@werner/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@werner/App/Components/Header/HeaderClickable";
import FloatingMenuTooltip from "@werner/App/Components/Menu/FloatingMenuTooltip";
import MiniCart from "@werner/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@werner/App/Store/Reducers";
import CartHeader from "@werner/XDL/Icons/CartHeader";
import { pluralize } from "@werner/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
    currencySymbol:
        getCurrentCartState(state).value?.currencySymbol || state.context.session.currency?.currencySymbol || "$",
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    floatingTooltipStyle?: FloatingTooltipStyles;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: CartHeader,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    orderSubTotalDisplay,
    currencySymbol,
    isTabletScreen,
}) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const history = useHistory();

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    const openCartPageHandler = () => {
        if (!cartUrl) {
            return;
        }
        history.push(cartUrl);
    };

    const headerClickable = (
        <HeaderClickable
            onClick={openCartPageHandler}
            iconProps={{ src: icon }}
            data-test-selector="cartLink"
            title={`${orderSubTotalDisplay ?? `${currencySymbol}0.00`}`}
            subTitle={`${retainedTotalCountToDisplay || 0} ${translate(
                pluralize(retainedTotalCountToDisplay || 0, "Item"),
            )}`}
            isOpen={isPopupShown}
            extendedStyles={styles.cartLink}
        />
    );

    if (isTabletScreen) {
        return headerClickable;
    }

    return (
        <FloatingMenuTooltip
            extendedStyles={styles.floatingTooltipStyle}
            maxWidth={750}
            triggerComponent={headerClickable}
            showArrow={true}
            offset={{ mainAxis: 16, crossAxis: 24 }}
            placement={"bottom-end"}
            isOpen={isPopupShown}
            onOpenChange={isOpen => setIsPopupShown(isOpen)}
            flip={{ fallbackPlacements: ["bottom-end"] }}
        >
            <MiniCart isPopupShown={isPopupShown} />
        </FloatingMenuTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
